import { ProductDataType } from "@/api/ProductsApi";
import { normalizeCabysCode } from "@utils/Cabys";
import {
    IVA_TAX_TYPE,
    MEASURE_UNITS,
    MERCHANDISE_PRODUCT_TYPE_CODE,
    SERVICE_PRODUCT_TYPE_CODE,
    TAXES_PERCENTAGES,
    TAXES_PERCENTAGES_CODE_BY_PERCENTAGE,
    TAXES_TYPES,
} from "@utils/Constants";
import { ProductImportValueKind, findProductImportField } from "./productImportFields";
import { ColumnMapping as GenericColumnMapping } from "./core/types";
import { clean, parseImportBoolean, parseImportDecimal, parseImportInteger, simplify } from "./core/values";

/** Columna del archivo -> atributo del producto. `null` = columna sin mapear. */
export type ProductColumnMapping = GenericColumnMapping;

const SERVICE_VALUES = new Set(["servicio", "servicios", "service", "s", "2"]);
const MERCHANDISE_VALUES = new Set([
    "producto",
    "productos",
    "mercancia",
    "mercaderia",
    "bien",
    "bienes",
    "articulo",
    "item",
    "m",
    "1",
]);

/** "Producto"/"Mercancia" -> 1, "Servicio" -> 2. `undefined` si no se reconoce el valor. */
export const parseProductType = (raw: unknown): number | undefined => {
    const value = simplify(raw);
    if (!value) return undefined;
    if (SERVICE_VALUES.has(value)) return SERVICE_PRODUCT_TYPE_CODE;
    if (MERCHANDISE_VALUES.has(value)) return MERCHANDISE_PRODUCT_TYPE_CODE;
    return undefined;
};

/**
 * Codigo de tipo de impuesto de Hacienda.
 *
 * Acepta el codigo tal cual ("01") o el nombre del catalogo. Cualquier variante que
 * mencione IVA cae en el codigo general, que es el unico que usan los importadores.
 */
export const parseTaxTypeCode = (raw: unknown): string | undefined => {
    const value = clean(raw);
    if (!value) return undefined;

    const catalog = TAXES_TYPES as Record<string, string>;
    if (catalog[value]) return value;

    const simplified = simplify(value);
    const byLabel = Object.keys(catalog).find((code) => simplify(catalog[code]) === simplified);
    if (byLabel) return byLabel;

    return simplified.includes("iva") ? IVA_TAX_TYPE : undefined;
};

/**
 * Codigo de tarifa de impuesto de Hacienda a partir de lo que trae el archivo.
 *
 * Los exportadores escriben el porcentaje ("13 %", "13", "0 %") y Hacienda espera el
 * codigo de la tarifa ("08", "01"). Tambien se acepta el codigo directo, salvo cuando el
 * valor viene con signo de porcentaje: ahi siempre se interpreta como porcentaje.
 */
export const parseTaxPercentageCode = (raw: unknown): string | undefined => {
    const value = clean(raw);
    if (!value) return undefined;

    const catalog = TAXES_PERCENTAGES as Record<string, string>;
    const hasPercentSign = value.includes("%");

    if (!hasPercentSign && /^\d{2}$/.test(value) && catalog[value]) return value;

    const percentage = parseImportDecimal(value);
    if (percentage !== undefined) {
        const code = TAXES_PERCENTAGES_CODE_BY_PERCENTAGE[`${percentage}`];
        if (code) return code;
    }

    return catalog[value] ? value : undefined;
};

/** Unidad de medida: acepta la clave del catalogo ("Unid") o su nombre ("Unidad"). */
export const parseMeasureUnit = (raw: unknown): string | undefined => {
    const value = clean(raw);
    if (!value) return undefined;

    const catalog = MEASURE_UNITS as Record<string, string>;
    if (catalog[value]) return value;

    const simplified = simplify(value);
    const byKey = Object.keys(catalog).find((key) => simplify(key) === simplified);
    if (byKey) return byKey;

    const byLabel = Object.keys(catalog).find((key) => simplify(catalog[key]) === simplified);
    return byLabel;
};

/** Aplica la normalizacion que corresponde al tipo del atributo destino. */
export const normalizeProductValue = (kind: ProductImportValueKind, raw: unknown): unknown => {
    switch (kind) {
        case "boolean":
            return parseImportBoolean(raw);
        case "integer":
            return parseImportInteger(raw);
        case "decimal":
            return parseImportDecimal(raw);
        case "cabys":
            return normalizeCabysCode(`${raw ?? ""}`);
        case "productType":
            return parseProductType(raw);
        case "taxTypeCode":
            return parseTaxTypeCode(raw);
        case "taxPercentageCode":
            return parseTaxPercentageCode(raw);
        case "measureUnit":
            return parseMeasureUnit(raw);
        case "code":
        case "categoryName":
        case "text":
        default:
            return clean(raw);
    }
};

/**
 * Aplica el mapeo de columnas a una fila cruda y devuelve los atributos del producto.
 *
 * La categoria se deja como nombre: resolverla a `categoryId` requiere crear las que
 * falten, y eso lo hace `resolveProductRows`. Los defaults tampoco se aplican aqui, porque
 * varios dependen de la categoria (ver `applyProductDefaults`).
 */
export const mapRowToProduct = (
    row: string[],
    mapping: ProductColumnMapping
): { product: ProductDataType; categoryName: string } => {
    const product: Record<string, unknown> = {};
    let categoryName = "";

    Object.entries(mapping).forEach(([columnIndex, fieldName]) => {
        if (!fieldName) return;

        const field = findProductImportField(fieldName);
        if (!field) return;

        const value = normalizeProductValue(field.kind, row[Number(columnIndex)]);

        if (field.kind === "categoryName") {
            categoryName = `${value ?? ""}`;
            return;
        }

        // Un valor vacio no debe pisar el default del modelo, salvo los booleanos, donde
        // "no" (y la celda en blanco) es informacion legitima.
        if (value === undefined || value === "") return;

        product[fieldName] = value;
    });

    return { product: product as ProductDataType, categoryName };
};

/** Atributos que el usuario mapeo explicitamente, para saber cuales defaults aplicar. */
export const mappedProductFields = (mapping: ProductColumnMapping): Set<string> =>
    new Set(Object.values(mapping).filter((field): field is string => !!field));
